import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useProfileQueries } from "./profile/useProfileQueries";
import { useProfileMutations } from "./profile/useProfileMutations";
import { useProfileUtils } from "./profile/useProfileUtils";

interface UserProfile {
  id: string;
  name: string | null;
  email: string | null;
  telefone: string | null;
  cpf: string | null;
  empresa_id: number | null;
  nivel: number | null;
  foto_perfil_url: string | null;
  created_at: string;
  empresas?: {
    nome_fantasia: string | null;
    razao_social: string | null;
    ativo?: boolean | null;
  } | null;
}

export function useUserProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { loadCompleteUserProfile } = useProfileQueries();
  const { isUpdating, updateProfile: updateProfileMutation } =
    useProfileMutations();
  const { isAdmin, getEmpresaName } = useProfileUtils();

  // Fetch user profile with React Query
  const { data: profile, isLoading } = useQuery({
    queryKey: ["user-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return null;
      }

      // Get current auth user for verification
      const {
        data: { user: currentUser },
        error: authError,
      } = await supabase.auth.getUser();

      if (authError) {
        console.error("Error fetching current user:", authError);
        return null;
      }

      if (!currentUser?.id) {
        console.error("auth.uid() is null");
        await supabase.auth.signOut();
        return null;
      }

      // Load complete profile with single query
      const completeProfile = await loadCompleteUserProfile(currentUser.id);
      return completeProfile;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const updateProfile = async (updates: {
    name?: string;
    telefone?: string;
    foto_perfil_url?: string | null;
  }) => {
    if (!user?.id) return false;

    const success = await updateProfileMutation(user.id, updates);
    if (success) {
      // Invalidate query to refetch updated profile
      queryClient.invalidateQueries({ queryKey: ["user-profile", user.id] });
    }
    return success;
  };

  return {
    profile,
    isLoading,
    isUpdating,
    isEmpresaLoading: false, // Sempre false pois carregamos tudo junto
    updateProfile,
    refetchProfile: () =>
      queryClient.invalidateQueries({ queryKey: ["user-profile", user?.id] }),
    isAdmin: () => isAdmin(profile),
    getEmpresaName: () => getEmpresaName(profile, false), // Sempre false pois carregamos tudo junto
  };
}
